import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GameState, ClientState } from '@youraiquiz/shared';
import { createInitialGameState } from '@youraiquiz/shared';

interface GameStore {
  // Server state (synced from server)
  gameState: GameState;

  // Client state (local only)
  clientState: ClientState;

  // Actions
  setGameState: (state: GameState) => void;
  setConnected: (connected: boolean) => void;
  setReconnecting: (reconnecting: boolean) => void;
  setMyId: (id: string | null) => void;
  setSessionToken: (token: string | null) => void;
  setError: (error: string | null) => void;
  reset: () => void;

  // Computed
  isHost: () => boolean;
  myPlayer: () => GameState['players'][0] | undefined;
}

const initialClientState: ClientState = {
  isConnected: false,
  isReconnecting: false,
  myId: null,
  sessionToken: null,
  error: null
};

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      gameState: createInitialGameState(),
      clientState: initialClientState,

      setGameState: (state) => {
        const prevStatus = get().gameState.status;
        console.log(`[STORE] Updating state: ${prevStatus} -> ${state.status}`);
        set({ gameState: state });
      },

      setConnected: (connected) =>
        set((s) => ({
          clientState: { ...s.clientState, isConnected: connected, error: null }
        })),

      setReconnecting: (reconnecting) =>
        set((s) => ({
          clientState: { ...s.clientState, isReconnecting: reconnecting }
        })),

      setMyId: (id) =>
        set((s) => ({
          clientState: { ...s.clientState, myId: id }
        })),

      setSessionToken: (token) =>
        set((s) => ({
          clientState: { ...s.clientState, sessionToken: token }
        })),

      setError: (error) =>
        set((s) => ({
          clientState: { ...s.clientState, error }
        })),

      reset: () =>
        set({
          gameState: createInitialGameState(),
          clientState: initialClientState
        }),

      isHost: () => {
        const { gameState, clientState } = get();
        return gameState.hostId === clientState.myId;
      },

      myPlayer: () => {
        const { gameState, clientState } = get();
        return gameState.players.find((p) => p.id === clientState.myId);
      }
    }),
    {
      name: 'youraiquiz-session',
      storage: {
        getItem: (name) => {
          const value = sessionStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        }
      },
      partialize: (state) => ({
        clientState: {
          myId: state.clientState.myId,
          sessionToken: state.clientState.sessionToken
        },
        gameState: {
          roomCode: state.gameState.roomCode
        }
      } as unknown as GameStore),
      // Merge persisted state with initial state to preserve default values
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<GameStore> | undefined;
        return {
          ...currentState,
          clientState: {
            ...currentState.clientState,
            myId: persisted?.clientState?.myId ?? null,
            sessionToken: persisted?.clientState?.sessionToken ?? null
          },
          gameState: {
            ...currentState.gameState,
            roomCode: persisted?.gameState?.roomCode ?? ''
          }
        };
      }
    }
  )
);

// Selectors for common use cases
export const useIsHost = () => useGameStore((s) => s.gameState.hostId === s.clientState.myId);
export const useMyPlayer = () =>
  useGameStore((s) => s.gameState.players.find((p) => p.id === s.clientState.myId));
export const useGameStatus = () => useGameStore((s) => s.gameState.status);
export const usePlayers = () => useGameStore((s) => s.gameState.players);
export const useCategories = () => useGameStore((s) => s.gameState.categories);
export const useQuestions = () => useGameStore((s) => s.gameState.questions);
export const useCurrentQuestion = () =>
  useGameStore((s) => s.gameState.questions[s.gameState.currentQuestionIndex]);
export const useIsConnected = () => useGameStore((s) => s.clientState.isConnected);
export const useIsReconnecting = () => useGameStore((s) => s.clientState.isReconnecting);
export const useError = () => useGameStore((s) => s.clientState.error);
